import { viewportObserver } from '@dna/utils'

/**
 * @typedef {Object} FocusTrapMixinInterface
 * @property {Function} setupFocusTrap - Sets up the focus trap behavior for the component.
 */

/**
 * A mixin that provides focus trap functionality for Lit components.
 * @polymer
 * @mixinFunction
 * @appliesMixin LitElement
 * @param {typeof LitElement} superClass - The base class to extend.
 * @return {FocusTrapMixinInterface & typeof LitElement} The extended class with focus trap functionality.
 */
export const FocusTrapMixin = (superClass) =>
  class extends superClass {
    firstUpdated() {
      super.firstUpdated()
      this._setupFocusTrap()
    }

    /**
     * Sets up the focus trap behavior for the component.
     * Restricts focus to focusable elements within the component's shadow root.
     */
    _setupFocusTrap() {
      const focusableElementsSelector =
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      const focusableElements = this.shadowRoot.querySelectorAll(
        focusableElementsSelector,
      )
      const firstFocusableElement = focusableElements[0]
      const lastFocusableElement =
        focusableElements[focusableElements.length - 1]

      /**
       * Handles the keydown event to trap focus within the component.
       * @param {KeyboardEvent} e - The keydown event object.
       */
      this.addEventListener('keydown', (e) => {
        console.log(e.key)
        if (e.key === 'Tab') {
          // Shift + Tab
          if (e.shiftKey) {
            if (document.activeElement === firstFocusableElement) {
              e.preventDefault()
              lastFocusableElement.focus()
            }
            // Tab
          } else {
            if (document.activeElement === lastFocusableElement) {
              e.preventDefault()
              firstFocusableElement.focus()
            }
          }
        }
      })
    }
  }

export const WithProfiler = (superClass) =>
  class ProfiledElement extends superClass {
    static instanceNumber = 0

    constructor() {
      super()
      this.instanceId = ProfiledElement.instanceNumber++
      this.profiler_fields = []
    }

    willUpdate(changedProperties) {
      const performanceEnabled =
        window?.performance?.mark && window?.performance?.measure
      if (performanceEnabled) {
        window.performance.mark(
          `${this.constructor.name}:${this.instanceId}:start`,
        )
        this.updateComplete.then(() => {
          window.requestAnimationFrame(() => {
            try {
              window.performance.measure(
                `${this.constructor.name}:${this.instanceId}`,
                {
                  start: `${this.constructor.name}:${this.instanceId}:start`,
                  detail: {
                    devtools: {
                      trackGroup: 'Rothys',
                      track: 'Lit Render',
                      properties: [
                        ['Component', `${this.constructor.name}`],
                        [
                          'Render Reasons',
                          Array.from(changedProperties.keys()).join(', '),
                        ],
                        ...this.profiler_fields.map((field) => [
                          field,
                          JSON.stringify(this[field]),
                        ]),
                      ],
                    },
                  },
                },
              )
            } catch (e) {
              console.error(e)
            }
          })
        })
      }
      return super.willUpdate()
    }
  }

/**
 * @typedef {Object} RespondToViewport
 * @property {Function} _defaultViewportChangeHandler
 * Given a set of breakpoint instructions, updates common viewport-related properties for a component.
 * @param {string} newViewport - A string representing the updated viewport size.
 */

/**
 * A mixin that checks for mobile viewport, allowing components to respond accordingly.
 * @polymer
 * @mixinFunction
 * @appliesMixin LitElement
 * @param {typeof LitElement} superClass - The base class to extend.
 * @return {RespondToViewport & typeof LitElement} The extended class with focus trap functionality.
 */

export const RespondToViewport = (superClass) =>
  class extends superClass {
    static properties = {
      isMobile: { type: Boolean },
      viewport: { type: String },
    }

    constructor() {
      super()

      this.isMobile = false
      this.mobileBreakpoints = ['mobile']
      this.viewport = viewportObserver.currentViewport

      this._setMobileBreakpoints(['mobile'])
      this._handleViewportChange = this._handleViewportChange.bind(this)
    }

    connectedCallback() {
      super.connectedCallback()
      viewportObserver.addListener(this._handleViewportChange)
    }

    disconnectedCallback() {
      super.disconnectedCallback()
      viewportObserver.removeListener(this._handleViewportChange)
    }

    _handleViewportChange(newViewport) {
      this.viewport = newViewport
      this.isMobile = this.mobileBreakpoints.includes(newViewport)
    }

    _setMobileBreakpoints(breakpoints) {
      if (breakpoints.length) {
        this.mobileBreakpoints = breakpoints
      }
      this._handleViewportChange(this.viewport)
    }
  }
